// Reactive core — tagged-template engine. Isomorphic.
//
// `html` captures a template's static strings + dynamic values. The SAME hole
// classification drives both server serialization (`renderToString`) and the
// browser's clone/hydrate path (src/client/dom.ts), so the DOM structure the
// server emits lines up node-for-node with what the client expects — that is
// what makes true hydration (adopt, don't re-render) possible.
//
// Binding conventions inside a tag:
//   name=${v}     attribute
//   .prop=${v}    DOM property
//   @event=${fn}  event listener
//   ?attr=${v}    boolean attribute
// Anywhere else, ${v} is a child/node binding (text, template, array, repeat).

import { read } from "./signal";

const TEMPLATE_BRAND = Symbol.for("hc.template");
const REPEAT_BRAND = Symbol.for("hc.repeat");
const RAW_BRAND = Symbol.for("hc.raw");

export interface UnsafeHtml {
  readonly [RAW_BRAND]: true;
  readonly value: string;
}

/** Insert pre-rendered HTML verbatim (used for server-composed child components). */
export function unsafeHtml(value: string): UnsafeHtml {
  return { [RAW_BRAND]: true, value };
}

export function isUnsafeHtml(x: unknown): x is UnsafeHtml {
  return typeof x === "object" && x !== null && (x as Record<symbol, unknown>)[RAW_BRAND] === true;
}

export interface TemplateResult {
  readonly [TEMPLATE_BRAND]: true;
  readonly strings: TemplateStringsArray;
  readonly values: readonly unknown[];
}

export interface RepeatResult<T = unknown> {
  readonly [REPEAT_BRAND]: true;
  readonly items: readonly T[];
  readonly key: (item: T, index: number) => unknown;
  readonly template: (item: T, index: number) => TemplateResult;
}

export type PartType = "node" | "attr" | "prop" | "event" | "bool";

export interface PartMeta {
  index: number;
  type: PartType;
  /** Clean name (prefix stripped) for attr/prop/event/bool. */
  name: string;
  /** Whether the attribute value was wrapped in quotes in the source. */
  quoted: boolean;
}

export function html(strings: TemplateStringsArray, ...values: unknown[]): TemplateResult {
  return { [TEMPLATE_BRAND]: true, strings, values };
}

export function isTemplateResult(x: unknown): x is TemplateResult {
  return typeof x === "object" && x !== null && (x as Record<symbol, unknown>)[TEMPLATE_BRAND] === true;
}

/** Keyed list. Updates by key with minimal DOM moves on the client. */
export function repeat<T>(
  items: readonly T[],
  key: (item: T, index: number) => unknown,
  template: (item: T, index: number) => TemplateResult,
): RepeatResult<T> {
  return { [REPEAT_BRAND]: true, items, key, template };
}

export function isRepeatResult(x: unknown): x is RepeatResult {
  return typeof x === "object" && x !== null && (x as Record<symbol, unknown>)[REPEAT_BRAND] === true;
}

// --- Hole classification (shared by SSR and client compile) ----------------

const ATTR_TAIL = /\s([.@?]?[-\w:]+)=\s*("|')?$/;

/** Is the cursor currently inside an open tag (between `<` and `>`)? */
function insideTag(text: string): boolean {
  return text.lastIndexOf("<") > text.lastIndexOf(">");
}

export function classifyHoles(strings: readonly string[]): PartMeta[] {
  const parts: PartMeta[] = [];
  let prefix = "";
  for (let i = 0; i < strings.length - 1; i++) {
    prefix += strings[i];
    if (insideTag(prefix)) {
      const m = ATTR_TAIL.exec(strings[i] ?? "");
      const raw = m?.[1] ?? "";
      const quoted = Boolean(m?.[2]);
      let type: PartType = "attr";
      let name = raw;
      if (raw.startsWith("@")) {
        type = "event";
        name = raw.slice(1);
      } else if (raw.startsWith(".")) {
        type = "prop";
        name = raw.slice(1);
      } else if (raw.startsWith("?")) {
        type = "bool";
        name = raw.slice(1);
      }
      parts.push({ index: i, type, name, quoted });
    } else {
      parts.push({ index: i, type: "node", name: "", quoted: false });
    }
  }
  return parts;
}

/**
 * Build the client-side template skeleton (markers, no values). Node holes get
 * adjacent start/end comments; attribute holes get a `data-hcb-N` locator and
 * the original `name=` token is stripped.
 */
export function templateHtml(strings: readonly string[], parts: PartMeta[]): string {
  let out = "";
  let stripQuote = false;
  for (let i = 0; i < strings.length; i++) {
    let s = strings[i] ?? "";
    if (stripQuote) {
      if (s[0] === '"' || s[0] === "'") s = s.slice(1);
      stripQuote = false;
    }
    const part = parts[i];
    if (!part) {
      out += s;
      continue;
    }
    if (part.type === "node") {
      out += `${s}<!--hc:${i}--><!--/hc:${i}-->`;
    } else {
      out += `${s.replace(ATTR_TAIL, "")} data-hcb-${i}`;
      if (part.quoted) stripQuote = true;
    }
  }
  return out;
}

// --- Server-side serialization --------------------------------------------

const escapeTextMap: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;" };
const escapeAttrMap: Record<string, string> = { "&": "&amp;", '"': "&quot;", "<": "&lt;", ">": "&gt;" };

export function escapeText(s: string): string {
  return s.replace(/[&<>]/g, (c) => escapeTextMap[c] ?? c);
}
export function escapeAttr(s: string): string {
  return s.replace(/[&"<>]/g, (c) => escapeAttrMap[c] ?? c);
}

function nodeToString(value: unknown): string {
  const v = read(value);
  if (v == null || v === false || v === true) return "";
  if (isUnsafeHtml(v)) return v.value;
  if (isTemplateResult(v)) return renderToString(v);
  if (isRepeatResult(v)) {
    return v.items.map((item, i) => renderToString(v.template(item, i))).join("");
  }
  if (Array.isArray(v)) return v.map(nodeToString).join("");
  return escapeText(String(v));
}

/** Serialize a TemplateResult to HTML, emitting the same markers the client expects. */
export function renderToString(result: TemplateResult): string {
  const { strings, values } = result;
  const parts = classifyHoles(strings as unknown as string[]);
  let out = "";
  let stripQuote = false;
  for (let i = 0; i < strings.length; i++) {
    let s = strings[i] ?? "";
    if (stripQuote) {
      if (s[0] === '"' || s[0] === "'") s = s.slice(1);
      stripQuote = false;
    }
    const part = parts[i];
    if (!part) {
      out += s;
      continue;
    }
    const value = values[i];
    if (part.type === "node") {
      out += `${s}<!--hc:${i}-->${nodeToString(value)}<!--/hc:${i}-->`;
      continue;
    }
    const head = s.replace(ATTR_TAIL, "");
    if (part.quoted) stripQuote = true;
    if (part.type === "attr") {
      out += `${head} ${part.name}="${escapeAttr(String(read(value) ?? ""))}" data-hcb-${i}`;
    } else if (part.type === "bool") {
      out += `${head}${read(value) ? ` ${part.name}` : ""} data-hcb-${i}`;
    } else {
      // event / prop — not serializable to HTML; keep the locator only.
      out += `${head} data-hcb-${i}`;
    }
  }
  return out;
}
